import type {Optional} from '@yandex-delivery/types'
import {
  applySpec,
  defaultTo,
  flow,
  get,
  ifElse,
  isUndefined,
  stubString,
  toCentimeter,
  toDefaultEmptyString,
  toNumber,
  toUndefined,
} from '@yandex-delivery/utils'

import type {Product, SddClaimProduct} from '../types'

// По умолчанию используется значение '1' для количества
const toDefaultCount = defaultTo('1')

/**
 * Создает начальный объект продукта с пустыми или стандартными полями.
 */
const toInitialProductField: () => Product = applySpec<Product>({
  name: stubString,
  price: stubString,
  count: toDefaultCount,
  width: toUndefined,
  height: toUndefined,
  length: toUndefined,
  weight: toUndefined,
  vendorCode: stubString,
  inn: stubString,
})

/**
 * Преобразует размер, если он существует, в сантиметры и возвращает его как строку.
 */
const toDimensionIfExists: (dimension?: number) => Optional<string> = ifElse(
  isUndefined,
  toUndefined,
  flow(toNumber, toCentimeter, String),
)

/**
 * Преобразует вес в строку, если он существует.
 */
const toWeight: (weight?: number) => Optional<string> = ifElse(isUndefined, toUndefined, String)

/**
 * Создает объект продукта на основе информации о грузовом товаре.
 */
const toFilledProduct: (cargoItem?: SddClaimProduct) => Product = applySpec<Product>({
  name: get('title'),
  price: get('cost_value'),
  count: flow(get('quantity'), toDefaultCount),
  width: flow(get('size.width'), toDimensionIfExists),
  height: flow(get('size.height'), toDimensionIfExists),
  length: flow(get('size.length'), toDimensionIfExists),
  weight: flow(get('weight'), toWeight),
  vendorCode: get('fiscalization.article'),
  inn: flow(get('fiscalization.supplier_inn'), toDefaultEmptyString),
})

/**
 * Создает объект продукта на основе грузового товара или возвращает начальный объект продукта.
 */
export const toProduct: (cargoItem?: SddClaimProduct) => Product = ifElse(
  isUndefined,
  toInitialProductField,
  toFilledProduct,
)
